import { useEffect, useState } from 'react'
import type React from 'react'

/**
 * Tracks which section id is in the reading position.
 * Pass the scrolling container's ref when the sections scroll inside an element
 * rather than the page.
 */
export function useActiveSection(
  ids: string[],
  containerRef?: React.RefObject<HTMLElement | null>,
  enabled = true,
): string {
  const key = ids.join('|')
  const [active, setActive] = useState(ids[0] ?? '')

  useEffect(() => {
    if (!enabled) return
    const sectionIds = key.split('|').filter(Boolean)
    if (sectionIds.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive(visible[0].target.id)
      },
      {
        root: containerRef?.current ?? null,
        rootMargin: '-12% 0px -72% 0px',
        threshold: 0,
      },
    )

    sectionIds.forEach((id) => {
      const element = document.getElementById(id)
      if (element) observer.observe(element)
    })

    return () => observer.disconnect()
  }, [key, enabled, containerRef])

  return active
}
